import { createColumnHelper } from '@tanstack/react-table';
import { CircleDollarSign, CloudCog, Info, Pencil, Trash2, Users } from 'lucide-react';

import {
  ScrollableCell,
  ScrollableCellWithAvatar,
} from '@/components/data-table/cells/scrollable-cell';
import { Button } from '@/components/ui/button';
import i18n from '@/core/i18n';
import { formatTableDate } from '@/lib/constants/table-display';
import { AllowedAction } from '@/modules/common/types/allowed-actions';

import { ChangedFieldsCell } from '../components/changed-fields';
import { TeamMemberChangeLogEntry, TeamMemberChangeType } from '../types/team-member-changes';

const columnHelper = createColumnHelper<TeamMemberChangeLogEntry>();

export const getChangesTypeMap = () => ({
  [TeamMemberChangeType.Info]: {
    icon: <Info size={18} />,
    label: i18n.t('authenticated.team:details.tabs.changes.info'),
  },
  [TeamMemberChangeType.Billing]: {
    icon: <CircleDollarSign size={18} />,
    label: i18n.t('authenticated.team:details.tabs.changes.billing'),
  },
  [TeamMemberChangeType.Capacity]: {
    icon: <Users size={18} />,
    label: i18n.t('authenticated.team:details.tabs.changes.capacity'),
  },
  [TeamMemberChangeType.Integration]: {
    icon: <CloudCog size={18} />,
    label: i18n.t('authenticated.team:details.tabs.changes.integration'),
  },
});

export const getTeamMemberChangesColumns = ({
  handleEditClick,
  handleDeleteClick,
  isEditEnabled,
}: {
  handleEditClick: (row: TeamMemberChangeLogEntry) => void;
  handleDeleteClick: (row: TeamMemberChangeLogEntry) => void;
  isEditEnabled: boolean;
}) => [
  columnHelper.accessor('type', {
    header: i18n.t('authenticated.team:details.tabs.changes.table.type'),
    size: 100,
    minSize: 100,
    cell: ({ getValue }) => {
      const typeMapper = getChangesTypeMap();
      const typeData = typeMapper[getValue()];
      return (
        <div className="flex min-w-0 items-center gap-2">
          <div className="shrink-0">{typeData.icon}</div>
          <ScrollableCell text={typeData.label} />
        </div>
      );
    },
  }),

  columnHelper.accessor('startDate', {
    header: i18n.t('authenticated.team:details.tabs.changes.table.startDate'),
    minSize: 100,
    size: 100,
    cell: ({ getValue }) => formatTableDate(getValue()),
  }),

  columnHelper.accessor('endDate', {
    header: i18n.t('authenticated.team:details.tabs.changes.table.endDate'),
    minSize: 100,
    size: 100,
    cell: ({ getValue }) => formatTableDate(getValue()),
  }),

  columnHelper.accessor('changedBy', {
    header: i18n.t('authenticated.team:details.tabs.changes.table.changedBy'),
    cell: ({ getValue }) => {
      const changedBy = getValue();
      return (
        <ScrollableCellWithAvatar
          text={changedBy.name}
          avatarName={changedBy.name}
          image={changedBy.profileImageUrl ?? undefined}
        />
      );
    },
  }),

  columnHelper.accessor('changedFields', {
    header: () => (
      <div className="pl-4">
        {i18n.t('authenticated.team:details.tabs.changes.table.changedFields')}
      </div>
    ),
    size: 350,
    minSize: 300,
    cell: ({ row }) => <ChangedFieldsCell entry={row.original} />,
  }),

  columnHelper.display({
    id: 'actions',
    minSize: 100,
    header: () => (
      <div className="w-full text-center">
        {i18n.t('authenticated.team:details.tabs.changes.table.action')}
      </div>
    ),
    cell: ({ row }) => {
      const allowedActions = row.original.allowedActions;
      const canEdit = isEditEnabled && allowedActions.includes(AllowedAction.EDIT);
      const canDelete =
        isEditEnabled &&
        allowedActions.includes(AllowedAction.DELETE) &&
        row.original.type !== TeamMemberChangeType.Integration;

      return (
        <div className="flex items-center justify-end">
          {canEdit && (
            <Button variant="ghost" size="icon" onClick={() => handleEditClick(row.original)}>
              <Pencil />
            </Button>
          )}
          {canDelete && (
            <Button variant="ghost" size="icon" onClick={() => handleDeleteClick(row.original)}>
              <Trash2 />
            </Button>
          )}
        </div>
      );
    },
  }),
];
